import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { formatEUR, formatKgPrice } from "@/lib/format";
import { QualitaetLogo } from "@/components/QualitaetLogo";
import { useCart } from "@/lib/cart";
import { Seo } from "@/components/Seo";
import { FadeUp, StaggerWords } from "@/components/Motion";
import { ArrowLeft, ArrowUpRight, Minus, Plus, Leaf, User, Sparkles, Award, MapPin } from "lucide-react";

export default function Product() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => { api.get(`/products/${slug}`).then((r) => setProduct(r.data)); }, [slug]);

  if (!product) return <div className="pt-40 text-center text-cream/50">Loading...</div>;
  const name = pickLocalized(product, lang, "name");
  const tagline = pickLocalized(product, lang, "tagline");
  const desc = pickLocalized(product, lang, "description");

  const qualities = [
    { icon: Leaf, title: t.product.q1, sub: t.product.q1s },
    { icon: User, title: t.product.q2, sub: t.product.q2s },
    { icon: Sparkles, title: t.product.q3, sub: t.product.q3s },
    { icon: Award, title: t.product.q4, sub: t.product.q4s },
  ];

  return (
    <div className="pt-28 pb-32 min-h-screen bg-[#0A0A0A]" data-testid="product-page">
      <Seo title={`${name} — Madubau Dolomiti`} description={desc} path={`/product/${product.slug}`} />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <Link to="/shop" className="inline-flex items-center gap-2 font-display text-[10px] tracking-[0.3em] text-cream/60 hover:text-gold mb-14" data-testid="back-to-shop">
          <ArrowLeft className="w-3 h-3" /> {t.product.back}
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT — Product image */}
          <FadeUp className="lg:col-span-7">
            <div className="relative">
              {product.limited && (
                <div className="absolute top-6 right-6 z-10 font-display text-[10px] tracking-[0.35em] text-black bg-gold px-4 py-2 clip-tag" data-testid="pd-limited-badge">
                  · {t.product.limited} ·
                </div>
              )}
              <div className="aspect-square overflow-hidden bg-[#0d0d0d] relative p-6">
                <div className="absolute inset-0 hero-glow" />
                <img src={product.image_url} alt={name} className="relative w-full h-full object-contain" />
              </div>
              <div className="mt-4 flex items-center justify-between font-display text-[9px] tracking-[0.4em] text-cream/40">
                <span>MADUBAU · DOLOMITI</span>
                <span>№ {product.slug.slice(0, 6).toUpperCase()}</span>
              </div>
              {product.video_url && (
                <div className="mt-8 aspect-[952/768] overflow-hidden bg-[#0d0d0d]" data-testid="pd-video">
                  <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                    <source src={`/videos/${product.slug}.webm`} type="video/webm" />
                    <source src={product.video_url} type="video/mp4" />
                  </video>
                </div>
              )}
            </div>
          </FadeUp>

          {/* RIGHT — Details */}
          <div className="lg:col-span-5 lg:pt-6">
            <FadeUp delay={0.1}>
              <div className="inline-flex items-center gap-2 font-editorial italic text-gold/90 text-sm mb-5">
                <MapPin className="w-3.5 h-3.5" />
                <span>{product.origin}</span>
              </div>
              <h1 className="font-editorial italic font-light text-4xl lg:text-5xl xl:text-6xl text-cream leading-[1.05] mb-5">
                <StaggerWords text={name} />
              </h1>
              <p className="text-cream/60 font-body text-lg italic mb-8">— {tagline}</p>
            </FadeUp>

            {/* short description */}
            <FadeUp delay={0.15}>
              <p className="text-cream/75 font-body leading-relaxed mb-10">{desc}</p>
            </FadeUp>

            {/* Qualities */}
            <FadeUp delay={0.2}>
              <div className="border-y border-white/10 py-8 mb-10">
                <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-6">/ {t.product.qualities_title}</div>
                <ul className="space-y-5">
                  {qualities.map((q, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      className="flex items-start gap-4"
                      data-testid={`quality-${i}`}
                    >
                      <div className="mt-0.5 border border-gold/40 p-2 shrink-0">
                        <q.icon className="w-4 h-4 text-gold" strokeWidth={1.2} />
                      </div>
                      <div>
                        <div className="font-display text-[11px] tracking-[0.25em] text-cream mb-1">{q.title}</div>
                        <div className="text-cream/50 text-sm font-body leading-snug">{q.sub}</div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </FadeUp>

            {/* Meta — weight / notes */}
            <FadeUp delay={0.25}>
              <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
                <div>
                  <div className="font-display text-[9px] tracking-[0.35em] text-gold mb-2">{t.product.weight}</div>
                  <div className="text-cream font-editorial italic text-xl">{product.weight_g} g ℮</div>
                </div>
                <div>
                  <div className="font-display text-[9px] tracking-[0.35em] text-gold mb-2">{t.product.notes}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {product.tasting_notes.map((n) => (
                      <span key={n} className="font-editorial italic text-cream/80 text-xs border border-gold/30 px-2 py-1">{n}</span>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Price + qty + CTA */}
            <FadeUp delay={0.3}>
              <div className="flex items-end justify-between mb-6 pt-6 border-t border-white/10">
                <div>
                  <div className="font-display text-[9px] tracking-[0.35em] text-cream/50 mb-1">{t.product.price.toUpperCase()}</div>
                  <span className="font-editorial italic text-5xl text-gold leading-none">{formatEUR(product.price_eur, lang)}</span>
                  <div className="text-cream/40 text-xs font-body mt-2" data-testid="pd-base-price">{formatKgPrice(product.price_eur, product.weight_g, lang)}</div>
                </div>
                <div className="flex items-center border border-white/20">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-cream/70 hover:text-gold" data-testid="pd-qty-minus"><Minus className="w-3 h-3" /></button>
                  <span className="px-5 text-cream font-display text-sm">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="p-3 text-cream/70 hover:text-gold" data-testid="pd-qty-plus"><Plus className="w-3 h-3" /></button>
                </div>
              </div>

              <button
                onClick={() => add(product, qty)}
                data-testid="pd-add-to-cart"
                className="w-full py-5 bg-gold text-black font-display text-[11px] tracking-[0.35em] hover:bg-gold-light gold-shine inline-flex items-center justify-center gap-3 mb-4"
                style={{ background: "#C9A24C" }}
              >
                {t.product.add}
                <ArrowUpRight className="w-4 h-4" />
              </button>

              <div className="flex items-center justify-center gap-6 text-[10px] font-display tracking-[0.3em] text-cream/40 mt-4">
                <span>✓ {t.product.trust_pay.toUpperCase()}</span>
                <span>✓ {t.product.trust_ship.toUpperCase()}</span>
                <span>✓ {t.product.trust_brand.toUpperCase()}</span>
              </div>
              <div className="mt-6 flex justify-center" data-testid="pd-quality-seal">
                <QualitaetLogo size="md" testid="pd-qualitaet-logo" />
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </div>
  );
}
